import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FiHome,
  FiBarChart2,
  FiBookOpen,
  FiUser,
  FiLayout,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiSearch,
} from "react-icons/fi";
import { FiMessageSquare } from "react-icons/fi";
import { useSidebar } from "../contexts/SidebarContext";
import { useClearChat } from "../contexts/ClearChatContext";
import ChatHistoryCard from "../components/common/ChatHistoryCard";

const HISTORY_TITLE_LIMIT = 60;
const HISTORY_PREVIEW_LIMIT = 80;

const normalizeWhitespace = (value) =>
  typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";

const truncateText = (value, limit) => {
  if (!value || value.length <= limit) {
    return value || "";
  }
  return `${value.slice(0, limit - 3)}...`;
};

const getHistoryMessageText = (message) => {
  if (!message || typeof message !== "object") {
    return "";
  }

  const imageContext = message.imageContext || "Image shared";
  const fileContext = message.fileContext || "File shared";
  const voiceContext = message.voiceContext || "Voice message";
  const liveContext = message.liveCameraContext || "Live camera assistance";
  const ocrContext = message.ocrContext || "OCR extracted text";

  if (message.hasImage) {
    return message.imageName
      ? `${imageContext} - ${message.imageName}`
      : imageContext;
  }

  if (message.hasFile) {
    return message.fileName
      ? `${fileContext} - ${message.fileName}`
      : fileContext;
  }

  if (message.hasVoice) {
    return voiceContext;
  }

  if (message.hasLiveCamera) {
    return liveContext;
  }

  if (message.hasOCR && message.ocrExtractedText) {
    return normalizeWhitespace(message.ocrExtractedText);
  }

  if (
    typeof message.content === "string" &&
    message.content.trim().length > 0
  ) {
    return normalizeWhitespace(message.content);
  }

  return "";
};

const normalizeRecentHistory = (history, fallbackStart) => {
  if (!Array.isArray(history)) {
    return [];
  }

  return history
    .map((message, index) => {
      if (!message || typeof message !== "object") {
        return null;
      }

      let timestamp = message.timestamp;
      if (typeof timestamp === "string") {
        timestamp = new Date(timestamp).getTime();
      }
      if (!Number.isFinite(timestamp)) {
        timestamp = fallbackStart + index * 1000;
      }

      return {
        ...message,
        timestamp,
      };
    })
    .filter(Boolean);
};

const buildHistoryCardEntry = (entry, referenceTime) => {
  const fallbackNow = Number.isFinite(referenceTime)
    ? referenceTime
    : Date.now();
  const normalizedHistory = normalizeRecentHistory(
    entry.recentHistory,
    fallbackNow
  );

  const lastMessage =
    [...normalizedHistory]
      .reverse()
      .find((message) => getHistoryMessageText(message).length > 0) || null;

  const firstUserMessage =
    normalizedHistory.find(
      (message) =>
        message.role === "user" && getHistoryMessageText(message).length > 0
    ) || null;

  const previewText = getHistoryMessageText(lastMessage);
  const rawTitle =
    getHistoryMessageText(firstUserMessage) ||
    previewText ||
    normalizeWhitespace(entry.title) ||
    "Untitled chat";

  const normalizedTitle = truncateText(rawTitle, HISTORY_TITLE_LIMIT);
  const normalizedPreview = truncateText(previewText, HISTORY_PREVIEW_LIMIT);

  const updatedAt =
    lastMessage?.timestamp ||
    normalizedHistory[normalizedHistory.length - 1]?.timestamp ||
    entry.updatedAt ||
    fallbackNow;

  return {
    ...entry,
    title: normalizedTitle,
    preview: normalizedPreview,
    messageCount: normalizedHistory.length || entry.messageCount || 0,
    updatedAt,
    recentHistory: normalizedHistory,
  };
};

const Sidebar = () => {
  const { collapsed, isMobile, toggleSidebar, sidebarMode, closeHistoryView } =
    useSidebar();
  const { chatHistory, selectHistoryEntry } = useClearChat();
  const [searchTerm, setSearchTerm] = useState("");

  const isHistoryMode = sidebarMode === "history";
  const hasHistory = Array.isArray(chatHistory) && chatHistory.length > 0;
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath =
    typeof location?.pathname === "string" ? location.pathname : "";

  const fallbackHistory = useMemo(() => {
    const now = Date.now();
    const minute = 60 * 1000;

    const baseHistory = [
      {
        id: "demo-hcf-001",
        title: "HCF Doubt",
        preview:
          "Yes, your solution is absolutely correct! You've correctly identified the numbers...",
        recentHistory: [
          {
            role: "user",
            content: "is this correct",
            timestamp: 1763060836831,
            hasImage: true,
            imageName: "WhatsApp Image 2025-09-09 at 00.56.14_b65f41ce.jpg",
            imageContext: "User uploaded an image with this message",
          },
          {
            role: "assistant",
            content: "Failed to process the input. Please try again.",
            timestamp: 1763060837067,
          },
          {
            role: "user",
            content: "is this solution correct",
            timestamp: 1763060868370,
            hasImage: true,
            imageName: "WhatsApp Image 2025-09-09 at 00.56.14_b65f41ce.jpg",
            imageContext: "User uploaded an image with this message",
          },
          {
            role: "assistant",
            content:
              "Yes, your solution is absolutely correct! You've correctly identified the numbers to find the HCF...",
            timestamp: 1763060878857,
          },
          {
            role: "user",
            content: "tell me about evaoration",
            timestamp: 1763062647418,
          },
        ],
      },
      {
        id: "demo-bio-002",
        title: "Photosynthesis Recap",
        preview:
          "Here's a quick refresher on how plants convert sunlight into glucose and oxygen.",
        recentHistory: [
          {
            role: "user",
            content: "Why do most leaves look green?",
            timestamp: now - 50 * minute,
          },
          {
            role: "assistant",
            content:
              "Leaves appear green because chlorophyll absorbs red and blue wavelengths but reflects green light.",
            timestamp: now - 49 * minute,
          },
          {
            role: "user",
            content: "What happens to the glucose that is produced?",
            timestamp: now - 48 * minute,
          },
          {
            role: "assistant",
            content:
              "Plants use some glucose immediately for energy, convert a portion to starch for storage, and send the rest to growing tissues.",
            timestamp: now - 47 * minute,
          },
        ],
      },
      {
        id: "demo-geo-003",
        title: "Geometry Refresher",
        preview:
          "Let's review how to find the area of a triangle and work with isosceles sides.",
        recentHistory: [
          {
            role: "user",
            content:
              "Uploaded a sketch. Need help finding the area of this triangle.",
            timestamp: now - 8 * minute,
            hasImage: true,
            imageName: "triangle-problem.png",
            imageContext: "User sketched the triangle dimensions",
          },
          {
            role: "assistant",
            content:
              "The area is 54 cm^2 because Area = 1/2 * base (12 cm) * height (9 cm).",
            timestamp: now - 7 * minute,
          },
          {
            role: "user",
            content: "If the equal sides are 10 cm each, what's the perimeter?",
            timestamp: now - 6 * minute,
          },
          {
            role: "assistant",
            content:
              "Two equal sides of 10 cm plus the 12 cm base give a perimeter of 32 cm.",
            timestamp: now - 5 * minute,
          },
        ],
      },
    ];

    return baseHistory.map((entry) => buildHistoryCardEntry(entry, now));
  }, []);

  const normalizedHistory = useMemo(() => {
    const source = fallbackHistory;
    if (!Array.isArray(source)) {
      return [];
    }
    const fallbackTimestamp = Date.now();
    return source.map((entry) =>
      buildHistoryCardEntry(entry, entry?.updatedAt || fallbackTimestamp)
    );
  }, [chatHistory, fallbackHistory, hasHistory]);

  const filteredHistory = useMemo(() => {
    if (!isHistoryMode) {
      return normalizedHistory;
    }

    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return normalizedHistory;
    }

    return normalizedHistory.filter((item) => {
      const title = item?.title?.toLowerCase?.() || "";
      const preview = item?.preview?.toLowerCase?.() || "";
      return title.includes(term) || preview.includes(term);
    });
  }, [isHistoryMode, normalizedHistory, searchTerm]);

  const historyItemsToRender = filteredHistory;
  const hasRenderableHistory = historyItemsToRender.length > 0;

  useEffect(() => {
    if (!isHistoryMode) {
      setSearchTerm("");
    }
  }, [isHistoryMode]);

  const handleHistorySelect = useCallback(
    (historyEntry) => {
      if (!historyEntry) {
        return;
      }

      selectHistoryEntry(historyEntry);

      const onTutorRoute =
        typeof currentPath === "string" &&
        (currentPath.startsWith("/tutor") ||
          currentPath.startsWith("/practice/tutor"));

      if (!onTutorRoute) {
        navigate("/tutor");
      }

      closeHistoryView();
    },
    [closeHistoryView, currentPath, navigate, selectHistoryEntry]
  );

  if (isMobile) {
    return null;
  }

  const computedWidthClass = collapsed
    ? "w-[60px]"
    : isHistoryMode
    ? "w-[260px]"
    : "w-[190px]";

  const handleToggleClick = isHistoryMode ? closeHistoryView : toggleSidebar;
  return (
    <div
      className={`bg-white hidden md:flex h-full flex-col py-4 px-2 transition-all duration-300 ${computedWidthClass}`}
    >
      {/* Sidebar header */}
      <div
        className={`flex items-center justify-between ${
          isHistoryMode == "history" ? "" : "mb-8"
        } px-4`}
      >
        {/* 'Aanya' text - hidden when sidebar is collapsed */}
        {isHistoryMode ? (
          <div className="px-0">
            <h2 className="text-base font-semibold text-gray-900">History</h2>
            <p className="text-xs text-gray-500 w-9/10">
              Review your previous tutor chats
            </p>
          </div>
        ) : (
          <div className={`text-primary text-2xl ${collapsed ? "hidden" : ""}`}>
            <span className="font-bold">A</span>
            <span>anya</span>
          </div>
        )}
        {/* Collapse button - hidden on mobile views (md:block makes it visible only on medium screens and up) */}
        <button
          onClick={handleToggleClick}
          className="text-gray-700 hidden md:block"
        >
          {isHistoryMode ? (
            <FiX className="w-6 h-6" />
          ) : collapsed ? (
            <div className="flex items-center -space-x-1">
              <FiChevronRight
                className="w-6 h-6"
                style={{ marginRight: "-16px" }}
              />
              <FiChevronRight className="w-6 h-6" />
            </div>
          ) : (
            <div className="flex items-center -space-x-1">
              <FiChevronLeft className="w-6 h-6" />
              <FiChevronLeft
                className="w-6 h-6"
                style={{ marginLeft: "-12px" }}
              />
            </div>
          )}
        </button>
      </div>

      {isHistoryMode ? (
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="px-0 ">
            <div className="relative">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Search chats"
                aria-label="Search chat history"
              />
            </div>
          </div>
          <div className="mt-4 flex-1 overflow-y-auto px-0 pb-4">
            {hasRenderableHistory ? (
              historyItemsToRender
                .slice(0, 3)
                .map((item) => (
                  <ChatHistoryCard
                    key={item.id}
                    entry={item}
                    onSelect={handleHistorySelect}
                  />
                ))
            ) : (
              <div className="px-2 text-sm text-gray-500">
                No chat history yet. Start a conversation to see it here.
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Sidebar Navigation */}
          <nav className="flex flex-col gap-2">
            {/* NavLink for Home */}
            <NavLink
              to="/"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              {/* Icon - centered when collapsed */}
              <FiHome className={`w-6 h-6 ${collapsed ? "mx-auto" : ""}`} />
              {/* Text - hidden when collapsed */}
              {!collapsed && <span className="font-medium">Home</span>}
            </NavLink>

            {/* <NavLink
              to="/new-home"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <FiHome className={`w-6 h-6 ${collapsed ? "mx-auto" : ""}`} />
              {!collapsed && <span className="font-medium">NewHome</span>}
            </NavLink> */}

            {/* NavLink for Assessment */}
            {/* <NavLink
          to="assessment"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-xl ${isActive
              ? "bg-primary text-white"
              : "text-gray-700 hover:bg-gray-100"
            }`
          }
        >
          <FiBarChart2 className={`w-6 h-6 ${collapsed ? "mx-auto" : ""}`} />
          {!collapsed && <span className="font-medium">Assessment</span>}
        </NavLink> */}

            {/* NavLink for Learn (was Practice) */}
            {/* <NavLink
              to="/practice"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <FiBookOpen className={`w-6 h-6 ${collapsed ? "mx-auto" : ""}`} />
              {!collapsed && <span className="font-medium">Learn</span>}
            </NavLink> */}

            <NavLink
              to="/new-learn"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <FiBookOpen className={`w-6 h-6 ${collapsed ? "mx-auto" : ""}`} />
              {!collapsed && <span className="font-medium">Learn</span>}
            </NavLink>

            {/* NavLink for Progress (was Dashboard) */}
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <FiLayout className={`w-6 h-6 ${collapsed ? "mx-auto" : ""}`} />
              {!collapsed && <span className="font-medium">Progress</span>}
            </NavLink>
          </nav>

          {/* Bottom area */}
          <div className="mt-auto px-2">
            <NavLink
              to="/feedback"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <FiMessageSquare
                className={`w-6 h-6 ${collapsed ? "mx-auto" : ""}`}
              />
              {!collapsed && <span className="font-medium">Feedback</span>}
            </NavLink>
          </div>
        </>
      )}
    </div>
  );
};

export default Sidebar;
